import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SyncStatusPanel = ({ syncStatus, loading, onManualSync }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusIcon = () => {
    if (loading) return { name: 'Loader', color: 'var(--color-primary)' };
    if (syncStatus?.isConsistent) return { name: 'CheckCircle2', color: 'var(--color-success)' };
    return { name: 'AlertTriangle', color: 'var(--color-error)' };
  };

  const getStatusColor = () => {
    if (loading) return 'bg-primary/10 border-primary/20';
    if (syncStatus?.isConsistent) return 'bg-success/10 border-success/20';
    return 'bg-error/10 border-error/20';
  };

  const statusIcon = getStatusIcon();

  return (
    <div className="bg-card rounded-lg border border-border shadow-md">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/10">
              <Icon name="Activity" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Sync Status</h3>
              <p className="text-xs text-muted-foreground">Real-time data consistency monitoring</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            onClick={onManualSync}
            disabled={loading}
          >
            Manual Sync
          </Button>
        </div>
      </div>
      <div className="p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="ml-3 text-sm text-muted-foreground">Checking sync status...</p>
          </div>
        ) : syncStatus ? (
          <div className="space-y-4">
            {/* Overall Status */}
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${getStatusColor()}`}>
              <Icon name={statusIcon?.name} size={24} color={statusIcon?.color} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-foreground mb-1">
                  {syncStatus?.isConsistent ? 'All Systems Consistent' : 'Discrepancies Detected'}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {syncStatus?.isConsistent
                    ? 'Role data is synchronized across all authentication sources'
                    : `Found ${syncStatus?.discrepancies?.length || 0} discrepancy(ies) requiring attention`}
                </p>
              </div>
            </div>

            {/* Discrepancies List */}
            {syncStatus?.discrepancies && syncStatus?.discrepancies?.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Detected Issues</label>
                <div className="space-y-2">
                  {syncStatus?.discrepancies?.map((discrepancy, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-error/5 border border-error/20 rounded-lg">
                      <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-foreground">{discrepancy}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sync Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Data Sources</label>
                <p className="text-lg font-semibold text-foreground">3</p>
                <p className="text-xs text-muted-foreground">auth.users, JWT, user_profiles</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Consistency Status</label>
                <p className="text-lg font-semibold text-foreground">
                  {syncStatus?.isConsistent ? '100%' : `${Math.round((3 - (syncStatus?.discrepancies?.length || 0)) / 3 * 100)}%`}
                </p>
                <p className="text-xs text-muted-foreground">Data alignment</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Last Checked</label>
                <p className="text-sm font-medium text-foreground">{formatDate(syncStatus?.lastChecked)}</p>
              </div>
            </div>

            {/* Troubleshooting Tips */}
            {!syncStatus?.isConsistent && (
              <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg mt-4">
                <Icon name="Lightbulb" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <div className="text-xs text-foreground">
                  <p className="font-medium mb-1">Troubleshooting Steps</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Verify user has logged out and back in after role changes</li>
                    <li>Check if database migrations have been applied correctly</li>
                    <li>Ensure RLS policies allow proper role access</li>
                    <li>Review recent admin console changes for this user</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="AlertCircle" size={32} className="mx-auto mb-2 opacity-50" />
            <p>No sync status available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncStatusPanel;